import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar } from '../components/ListControls';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LendingPool {
  id: string;
  name: string;
  apyNum: number;
  apy: string;
  tvl: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const POOLS: LendingPool[] = [
  { id: 'pool-1', name: 'USDC Lending Pool', apyNum: 8.5, apy: '8.5%', tvl: '$85.2M' },
  { id: 'pool-2', name: 'XSGD Lending Pool', apyNum: 7.2, apy: '7.2%', tvl: '$40.3M' },
  { id: 'pool-3', name: 'USDC Prime Pool', apyNum: 6.8, apy: '6.8%', tvl: '$120.5M' },
  { id: 'pool-4', name: 'DAI Stable Pool', apyNum: 5.5, apy: '5.5%', tvl: '$65.8M' },
  { id: 'pool-5', name: 'USDC Growth Pool', apyNum: 9.2, apy: '9.2%', tvl: '$55.1M' },
  { id: 'pool-6', name: 'DAI Income Pool', apyNum: 7.8, apy: '7.8%', tvl: '$95.3M' },
  { id: 'pool-7', name: 'USDT Reserve Pool', apyNum: 6.1, apy: '6.1%', tvl: '$78.4M' },
  { id: 'pool-8', name: 'ABN Yield Pool', apyNum: 10.5, apy: '10.5%', tvl: '$28.7M' },
  { id: 'pool-9', name: 'FMM Opportunity Pool', apyNum: 8.9, apy: '8.9%', tvl: '$32.1M' },
  { id: 'pool-10', name: 'MSM Capital Pool', apyNum: 7.5, apy: '7.5%', tvl: '$45.6M' },
  { id: 'pool-11', name: 'SBC Lending Pool', apyNum: 11.2, apy: '11.2%', tvl: '$18.9M' },
  { id: 'pool-12', name: 'Meridian Income Pool', apyNum: 6.5, apy: '6.5%', tvl: '$52.3M' },
];

const WALLET_BALANCE = 125430.0;

function fmt(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Page Component ───────────────────────────────────────────────────────────

export function LenderDepositPage() {
  const [searchParams] = useSearchParams();
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(
    searchParams.get('poolId'),
  );
  const [amount, setAmount] = useState('');
  const [isDepositing, setIsDepositing] = useState(false);
  const [search, setSearch] = useState('');

  const pool = POOLS.find((p) => p.id === selectedPoolId);
  const parsedAmount = parseFloat(amount) || 0;

  const filteredPools = useMemo(
    () => POOLS.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [search],
  );

  function handleMax() {
    setAmount(WALLET_BALANCE.toString());
  }

  function handleDeposit() {
    if (!pool) return;
    if (parsedAmount <= 0) { toast.error('Please enter a valid amount'); return; }
    if (parsedAmount > WALLET_BALANCE) { toast.error('Insufficient balance'); return; }
    setIsDepositing(true);
    setTimeout(() => {
      setIsDepositing(false);
      setAmount('');
      setSelectedPoolId(null);
      toast.success(`Successfully deposited ${fmt(parsedAmount)} to ${pool.name}`);
    }, 2000);
  }

  const estAnnualYield = pool ? parsedAmount * (pool.apyNum / 100) : 0;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Deposit Stablecoins</h1>
          <p className="text-sm text-muted-foreground">
            Add stablecoins to a lending pool to earn yield.
          </p>
        </div>
      </div>

      {!pool ? (
        <>
          <SearchBar value={search} onChange={setSearch} placeholder="Search pools..." className="sm:w-64" />

          {filteredPools.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No pools found matching your search.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPools.map((p) => (
                <Card key={p.id}>
                  <CardHeader className="p-6">
                    <CardTitle>{p.name}</CardTitle>
                    <CardDescription>Earn yield by depositing stablecoins.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 pt-0 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">APY</span>
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        {p.apy}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">TVL</span>
                      <span className="text-sm font-semibold">{p.tvl}</span>
                    </div>
                    <Button className="w-full" onClick={() => setSelectedPoolId(p.id)}>
                      Select
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <Card className="mx-auto max-w-lg">
          <CardHeader className="p-6 pb-0">
            <CardTitle>{pool.name}</CardTitle>
            <CardDescription>
              Deposit stablecoins into this pool to earn {pool.apy} APY.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Wallet Balance</span>
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="mt-2 text-2xl font-bold">{fmt(WALLET_BALANCE)}</p>
              </div>
              <div className="rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Pool APY</span>
                  <Icon name="streamline:discount-percent-circle" className="h-4 w-4 text-ascend-success" />
                </div>
                <p className="mt-2 text-2xl font-bold text-ascend-success">{pool.apy}</p>
              </div>
            </div>

            {/* Amount input */}
            <div className="rounded-xl border border-border p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Amount to Deposit</span>
                <button
                  type="button"
                  onClick={handleMax}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-3.5 w-3.5" />
                  Max: {fmt(WALLET_BALANCE)}
                </button>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background">
                <span className="text-lg text-muted-foreground">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className="flex-1 bg-transparent text-lg font-medium outline-none placeholder:text-muted-foreground [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                />
                <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">USDC</span>
              </div>

              {/* Breakdown */}
              {parsedAmount > 0 && (
                <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Depositing</span>
                    <span className="font-semibold">{fmt(parsedAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Est. Annual Yield</span>
                    <span className="font-semibold text-ascend-success">+{fmt(estAnnualYield)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => { setSelectedPoolId(null); setAmount(''); }}
              >
                Cancel
              </Button>
              <Button
                disabled={isDepositing || parsedAmount <= 0 || parsedAmount > WALLET_BALANCE}
                onClick={handleDeposit}
              >
                {isDepositing ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Depositing...
                  </>
                ) : (
                  <>
                    <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="mr-2 h-4 w-4" />
                    Deposit
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
